'use client';

import { useLocale } from 'next-intl';
import NextLink from 'next/link';

import type { LocaleLink } from '@/components/Header';
import { languages, Link, usePathname } from '@/navigation';

type FooterLanguageLinksProps = {
  ariaLabel: string;
  localeLinks?: LocaleLink[];
};

const FooterLanguageLinks = ({
  ariaLabel,
  localeLinks,
}: FooterLanguageLinksProps) => {
  const locale = useLocale();
  const pathname = usePathname();
  const languageOptions = localeLinks
    ? localeLinks.flatMap((localeLink) => {
        const language = languages.find(
          (item) => item.locale === localeLink.locale,
        );

        return language ? [{ ...language, href: localeLink.href }] : [];
      })
    : languages.map((language) => ({ ...language, href: null }));

  return (
    <nav aria-label={ariaLabel}>
      <ul className="flex flex-wrap gap-x-6 gap-y-3">
        {languageOptions.map((language) => {
          const isCurrent = language.locale === locale;
          const className = `inline-flex rounded-sm text-sm font-medium leading-6 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-base-200 active:translate-y-px ${
            isCurrent
              ? 'text-primary'
              : 'text-base-content/60 hover:text-base-content'
          }`;

          return (
            <li key={language.locale}>
              {language.href ? (
                <NextLink
                  href={language.href}
                  hrefLang={language.code}
                  lang={language.code}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={className}
                >
                  {language.language}
                </NextLink>
              ) : (
                <Link
                  href={pathname || '/'}
                  locale={language.locale}
                  hrefLang={language.code}
                  lang={language.code}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={className}
                >
                  {language.language}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default FooterLanguageLinks;
